/**
 * advance-eligibility.helper.js
 *
 * Elegibilidad de estados y estado vigente del flujo para el avance lineal.
 * Lógica PURA de negocio (sin Vue, sin THREE, sin estado de componente) extraída
 * de RegisterAdvanceModal para que el modal y el gesto INLINE compartan una única
 * fuente de verdad (igual que advance-pool.helper ya centraliza las bolsas).
 *
 * Todas las funciones reciben explícitamente { statuses, previousSegments,
 * totalLength, orderMode } en lugar de leerlos de `this`.
 */

import { simulatePools } from './advance-pool.helper'

// Flag de comportamiento del RECHAZO en la máquina de estados. HARDCODEADO por
// ahora; TODO: moverlo al administrador de pictogramas (metadata de capa/pictograma)
// y pasarlo como argumento, igual que `orderMode`. Debe mantenerse en sync con la
// constante homónima de PictogramService (backend). Valores:
//   'reset'     → al inicio: cualquier rechazo vuelve a "sin ejecutar".
//   'step_back' → un paso atrás: vuelve a ofrecer el paso de decisión aceptar/
//                 rechazar ANTERIOR (conserva el avance previo).
// Lo usa currentFlowState (y debe ser coherente con computeStatusEligibility).
export const REJECT_RETURN_MODE = 'step_back'

const isRejectStatus = (s) => Number(s && s.weight) < 0

/** Metros ya registrados (suma cruda del histórico) para un status. */
export function existingMetersOfStatus(previousSegments, statusValue) {
  let total = 0
  for (const seg of previousSegments || []) {
    if (seg.status === statusValue) total += seg.meters || 0
  }
  return total
}

/**
 * Metros NETOS acumulados de un status en capas CON rechazos: se procesan los
 * segmentos en orden cronológico y un RECHAZO (weight<0) rechaza TODO lo
 * ejecutado → resetea el acumulado a 0. A diferencia de existingMetersOfStatus
 * (suma cruda de todo el histórico), esto refleja el ejecutado VIGENTE, que es lo
 * que define cuánto FALTA por registrar (totalLength − neto). El avance positivo
 * es parcial y acumulativo (10 + 20 = 30); tras un rechazo vuelve a 0.
 */
export function netMetersOfStatus(previousSegments, statuses, statusValue) {
  const isReject = (v) => {
    const s = (statuses || []).find(x => x.value === v)
    return s && Number(s.weight) < 0
  }
  const sorted = [...(previousSegments || [])]
    .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
  let net = 0
  for (const seg of sorted) {
    if (isReject(seg.status)) net = 0            // rechaza todo → reset
    else if (seg.status === statusValue) net += seg.meters || 0
  }
  return net
}

/**
 * AUTO-OBJETIVO del gesto inline multi-estado (Opción A — DECISIÓN REVISABLE).
 *
 * En el flujo CON rechazos (niveles en secuencia: Ejecutar → Aceptar → …), el gesto
 * apunta automáticamente al PRIMER nivel que todavía puede avanzar y lo marca DESDE
 * SU PROPIO FRENTE. Al completarse un nivel, el frente del siguiente es 0 → se marca
 * "desde el inicio", que es justo el comportamiento pedido.
 *
 *   - Nivel objetivo k = el menor k con tope>0, donde cap₀ = por-iniciar + rechazado
 *     y capₖ = bags[k-1] (lo que hay en el nivel anterior listo para aceptar).
 *   - appliedMeters (baseline del gesto) = frente del nivel k = Σ bags[j≥k].
 *   - Si ningún nivel tiene tope>0 → `completed:true` (no queda avance que registrar).
 *
 * Aplica a flujos SECUENCIALES (niveles encadenados): capas con rechazos, o modo de
 * orden 'strict'/'capped' (cada estado depende del anterior). En modo 'free'
 * (procesos paralelos, p. ej. zanjas) devuelve null y el llamador usa selección
 * libre de estado (baseline en el origen).
 *
 * ⚠️ Esta regla de auto-objetivo es revisable: si cambia el criterio (o se pasa a
 * "estado-primero"), tocar SOLO esta función. Ver memoria inline_advance_plan (F7).
 *
 * @param {{statuses:Array, previousSegments:Array, totalLength:number, orderMode:string}} args
 * @returns {?{level:number, statusValue:any, status:?object, appliedMeters:number, cap:number, completed:boolean}}
 */
export function computeAdvanceTarget({ statuses, previousSegments, totalLength, orderMode }) {
  const sts = statuses || []
  const sequential = sts.some(isRejectStatus) || orderMode === 'strict' || orderMode === 'capped'
  if (!sequential) return null   // modo libre / paralelo → selección libre en el llamador
  const L = totalLength || 0
  const eps = 1e-6
  const pools = simulatePools(previousSegments, sts, L)
  const { positives, bags, unstarted, rejected, M } = pools
  const frontOf = (k) => { let s = 0; for (let j = k; j < M; j++) s += bags[j] || 0; return s }
  const capOf = (k) => (k === 0 ? (unstarted || 0) + (rejected || 0) : (bags[k - 1] || 0))
  for (let k = 0; k < M; k++) {
    if (capOf(k) > eps) {
      const st = sts[positives[k]]
      return { level: k, statusValue: st.value, status: st, appliedMeters: frontOf(k), cap: capOf(k), completed: false }
    }
  }
  // Ningún nivel puede avanzar → segmento completado.
  return { level: -1, statusValue: null, status: null, appliedMeters: L, cap: 0, completed: true }
}

/**
 * Elegibilidad por estado (Map value → { eligible, cap }).
 *
 * DOS comportamientos según la capa:
 *  - CON rechazos (flujo aceptar/rechazar, ej. eléctrico): MODELO DE BOLSAS.
 *    Cada estado positivo tiene una "bolsa" = metros que llegaron a esa etapa y no
 *    avanzaron ni fueron rechazados (ver simulatePools). Ejecutar (nivel 0) toma de
 *    por-iniciar + rechazado; aceptar/rechazar del nivel k consume bags[k-1]. Todos
 *    los niveles con metros pendientes están abiertos a la vez, cada uno con su tope.
 *  - SIN rechazos (progresión normal): modelo clásico free/strict/capped.
 *
 * @param {{statuses:Array, previousSegments:Array, totalLength:number, orderMode:('free'|'strict'|'capped')}} args
 * @returns {Map<any,{eligible:boolean,cap:number}>}
 */
export function computeStatusEligibility({ statuses, previousSegments, totalLength, orderMode }) {
  const sts = statuses || []
  const L = totalLength || 0
  const mode = orderMode || 'free'
  const eps = 1e-6
  const map = new Map()
  const ex = (v) => existingMetersOfStatus(previousSegments, v)

  // Predecesor = POSITIVO anterior en la secuencia (salta rechazos).
  let lastPositiveIdx = -1
  const prevIdx = []
  for (let i = 0; i < sts.length; i++) {
    prevIdx[i] = lastPositiveIdx
    if (!isRejectStatus(sts[i])) lastPositiveIdx = i
  }

  const hasRejects = sts.some(isRejectStatus)

  if (!hasRejects) {
    // ---- CLÁSICO (sin rechazos): free / strict / capped ----
    for (let i = 0; i < sts.length; i++) {
      const existing = ex(sts[i].value)
      const p = prevIdx[i]
      let cap, eligible
      if (p < 0 || mode === 'free') {
        cap = Math.max(0, L - existing); eligible = cap > eps
      } else {
        const prevExisting = ex(sts[p].value)
        if (mode === 'strict') {
          const prevDone = L > 0 ? prevExisting + eps >= L : true
          cap = prevDone ? Math.max(0, L - existing) : 0
          eligible = prevDone && cap > eps
        } else { // capped
          cap = Math.max(0, prevExisting - existing); eligible = cap > eps
        }
      }
      map.set(sts[i].value, { eligible, cap })
    }
    return map
  }

  // ---- MODELO DE BOLSAS (con rechazos): flujo PARCIAL con retorno del rechazo ----
  const pools = simulatePools(previousSegments, sts, L)
  const posLevelOfIdx = pools.posLevelOfIdx
  const bags = pools.bags
  const executable = (pools.unstarted || 0) + (pools.rejected || 0)

  for (let i = 0; i < sts.length; i++) {
    let eligible = false
    let cap = 0
    if (!isRejectStatus(sts[i])) {
      const pk = posLevelOfIdx[i]
      if (pk === 0) {
        cap = executable
        eligible = cap > eps
      } else {
        cap = bags[pk - 1] || 0
        eligible = cap > eps
      }
    } else {
      // Rechazo: emparejado con su aceptación (prevIdx = índice del accept);
      // consume de la bolsa del nivel anterior a ESE accept.
      const acceptIdx = prevIdx[i]
      const kAccept = (acceptIdx >= 0) ? posLevelOfIdx[acceptIdx] : -1
      if (kAccept >= 1) {
        cap = bags[kAccept - 1] || 0
        eligible = cap > eps
      }
    }
    map.set(sts[i].value, { eligible, cap })
  }
  return map
}

/**
 * Estado NETO actual de la máquina de estados (solo capas con rechazos): el
 * positivo alcanzado procesando previousSegments en orden CRONOLÓGICO con la
 * MISMA regla de retroceso que computeStatusEligibility (REJECT_RETURN_MODE). El
 * segmento está en UN estado a la vez, no acumula. Devuelve
 * { value, label, color, meters } (meters = netMetersOfStatus del estado actual),
 * o null si no hay avance vigente (currentPosLevel < 0).
 *
 * @param {{statuses:Array, previousSegments:Array}} args
 * @returns {?{value:any,label:string,color:?string,meters:number}}
 */
export function currentFlowState({ statuses, previousSegments }) {
  const sts = statuses || []
  const hasRejects = sts.some(isRejectStatus)
  if (!hasRejects) return null

  let lastPositiveIdx = -1
  const prevIdx = []
  for (let i = 0; i < sts.length; i++) {
    prevIdx[i] = lastPositiveIdx
    if (!isRejectStatus(sts[i])) lastPositiveIdx = i
  }
  const positives = []
  const posLevelOfIdx = {}
  for (let i = 0; i < sts.length; i++) {
    if (!isRejectStatus(sts[i])) { posLevelOfIdx[i] = positives.length; positives.push(i) }
  }

  const valueToIdx = new Map(sts.map((s, i) => [s.value, i]))
  const segs = [...(previousSegments || [])].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0))
  let currentPosLevel = -1
  for (const seg of segs) {
    const i = valueToIdx.get(seg.status)
    if (i === undefined) continue
    if (isRejectStatus(sts[i])) {
      currentPosLevel = REJECT_RETURN_MODE === 'reset'
        ? -1
        : Math.max(-1, (posLevelOfIdx[prevIdx[i]] ?? 1) - 2)
    } else {
      currentPosLevel = posLevelOfIdx[i]
    }
  }
  if (currentPosLevel < 0) return null
  const st = sts[positives[currentPosLevel]]
  return { value: st.value, label: st.label, color: st.color, meters: netMetersOfStatus(previousSegments, sts, st.value) }
}
